/**
 * Shared variant option ordering.
 *
 * One canonical sort used by BOTH the top variant picker (product-info.js) and
 * the variant table (product-variant-table.js), so the two can never disagree.
 *
 * Why this exists
 * ---------------
 * The picker dropdown used to render `option.values` straight from Shopify, and
 * the table only sorted above 250 variants. Below that line nothing sorted at
 * all, so both views just mirrored whatever order the import script happened to
 * write -- and six different builders each define their own `size_sort_key`
 * that disagree with one another. Sorting at display time makes stored order
 * irrelevant: a bad import can no longer reach the storefront.
 *
 * The one rule that matters
 * -------------------------
 * NEVER reorder an option we don't have a canonical order for. `hasCanonicalOrder()`
 * is the gate; when it returns false the caller must leave the values exactly as
 * stored. An alphabetical fallback is worse than doing nothing -- it would turn
 * "Steel, Chrome" into "Chrome, Steel", "With Tang, Without Tang" into its
 * reverse, and "ANPT L-1, L-3, 6-Step" into "6-Step, L-1, L-3", which is the very
 * defect this file exists to fix.
 */
window.WPVariantSort = window.WPVariantSort || (function () {
    'use strict';

    function naturalCompare(a, b) {
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    }

    function normalize(s) {
        return (s == null ? '' : String(s)).trim().toLowerCase().replace(/\s+/g, '');
    }

    // Canonical Member ordering: GO, NO GO, then the both-ends / set value.
    var MEMBER_ORDER = {
        'go': 0, 'nogo': 1,
        'go/nogow/handle': 2, 'gononogow/handle': 2,
        'go/nogoset': 2, 'set': 2, 'doubleend': 2, 'go/nogo': 2,
    };

    // Canonical Class (tolerance grade) ordering, tightest -> loosest.
    var CLASS_ORDER = { 'xx': 0, 'x': 1, 'y': 2, 'z': 3, 'zz': 4 };

    // ---- Size ------------------------------------------------------------
    // Value groups. GAUGE always precedes INCH -- that isn't a preference, it's
    // magnitude: #0 (.060") through #12 (.216") are all smaller than 1/4".
    // METRIC is different. There's no objective way to interleave millimetres
    // with inches, so its placement is a per-product editorial choice and we
    // preserve whatever the product already does (see groupRanks).
    var G_METRIC = 'metric', G_PG = 'pg', G_GAUGE = 'gauge', G_INCH = 'inch', G_UNPARSED = 'unparsed';

    // A numbered size is written "N-TPI" with a whole number N -- the same shape
    // as an inch size like "1-8". They separate on TPI: every numbered size in
    // the unified series is 24 TPI or finer (#0-80 ... #12-24) and every
    // whole-inch diameter is 20 TPI or coarser (1-8, 1-20, 2-16).
    var GAUGE_TPI_MIN = 24;

    // Only applied when the option doesn't already mark gauge sizes with "#".
    // The taps/dies catalogs write "#2-56" explicitly, which makes a bare
    // "1-24" in that same list a 1-inch size, not a #1 -- inferring there would
    // hoist three inch sizes to the top of the list.
    function gaugeNumber(s, inferBare) {
        var m = s.match(/^#\s*(\d+)/);              // explicit "#4", "#10-32", "#0 - #1"
        if (m) return parseInt(m[1], 10);
        if (!inferBare) return null;
        m = s.match(/^(\d+)\s*-\s*(\d+)(?:\s|$)/);  // "10-24"; rejects "1/4-28", "1 1/2-6", "3.0000-2"
        if (m && parseInt(m[2], 10) >= GAUGE_TPI_MIN) return parseInt(m[1], 10);
        return null;
    }

    function usesExplicitGauge(values) {
        return values.some(function (v) { return /^\s*#/.test(String(v)); });
    }

    // Numeric value of one hyphen-separated part: "1 3/8" -> 1.375, "3/8" -> 0.375,
    // "11 1/2" -> 11.5, "#1" -> 1, ".150" -> 0.15. NaN when there's nothing to read.
    function partValue(p) {
        p = String(p == null ? '' : p).trim().replace(/^#\s*/, '');
        var m;
        if ((m = p.match(/^(\d+)\s+(\d+)\/(\d+)/))) {
            return parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10);
        }
        if ((m = p.match(/^(\d+)\/(\d+)/))) {
            return parseInt(m[1], 10) / parseInt(m[2], 10);
        }
        return parseFloat(p);
    }

    function sizeKey(s, inferBareGauge) {
        s = String(s == null ? '' : s).trim();
        var up = s.toUpperCase();

        if (/^M\d/.test(up)) {
            var mp = up.slice(1).split(/[-X]/);
            return [G_METRIC, parseFloat(mp[0]) || 0, parseFloat(mp[1]) || 0];
        }
        if (/^PG/.test(up)) {
            return [G_PG, parseFloat(up.replace(/PG/, '').replace(/-/g, '')) || 0, 0];
        }

        // The part after the hyphen is TPI on a thread size ("1/4-20") and the
        // upper bound on a range (".011 - .060"). Either way it is the right
        // secondary key: coarse threads sort before fine, narrow ranges before
        // wide ones. Without it every "0 - x" range ties on its lower bound and
        // the alphabetical tiebreak puts "0 - 1/2" ahead of "0 - 1/4".
        var parts = s.split('-');
        var second = parts.length > 1 ? partValue(parts.slice(1).join('-')) : 0;
        if (isNaN(second)) second = 0;

        var gauge = gaugeNumber(s, inferBareGauge !== false);
        if (gauge !== null) return [G_GAUGE, gauge, second];

        var val = partValue(parts[0]); // diameter / range lower bound
        if (isNaN(val)) return [G_UNPARSED, 0, 0];
        return [G_INCH, val, second];
    }

    /**
     * Rank each value group for one option, honouring the product's existing
     * metric-vs-inch arrangement.
     *
     * Taperlock lists metric first; Reversible lists it last; the Tri-Roll rolls
     * list inch first. All three are legitimate and none of them is what we were
     * asked to change, so we read the current arrangement from the stored order
     * rather than imposing one. Gauge-before-inch is not negotiable the same way
     * -- it's a size ordering, not a grouping.
     */
    function groupRanks(values, inferBareGauge) {
        var firstMetric = -1, firstInchish = -1;
        for (var i = 0; i < values.length; i++) {
            var g = sizeKey(values[i], inferBareGauge)[0];
            if (g === G_METRIC && firstMetric < 0) firstMetric = i;
            if ((g === G_GAUGE || g === G_INCH) && firstInchish < 0) firstInchish = i;
        }
        var metricLeads = firstMetric >= 0 && (firstInchish < 0 || firstMetric < firstInchish);
        var r = {};
        r[G_PG] = 0;
        if (metricLeads) {
            r[G_METRIC] = 1; r[G_GAUGE] = 2; r[G_INCH] = 3;
        } else {
            r[G_GAUGE] = 1; r[G_INCH] = 2; r[G_METRIC] = 3;
        }
        r[G_UNPARSED] = 9;
        return r;
    }

    // ---- Step Type / gage-length steps ------------------------------------
    // Handles a bare "L1" / "L-2" / "6-Step" as well as prefixed forms such as
    // "ANPT L-1". Returns null when a value isn't a step at all.
    function stepRank(v) {
        var s = String(v == null ? '' : v);
        var m = s.match(/(?:^|[\s(])L-?\s*(\d)\b/i);
        if (m) return parseInt(m[1], 10);
        if (/\b6\s*-?\s*step\b/i.test(s)) return 6;
        return null;
    }

    function isStepLike(values) {
        return values.length > 1 && values.every(function (v) { return stepRank(v) !== null; });
    }

    // ---- The gate ---------------------------------------------------------
    /**
     * True only when every value in the option maps to a known canonical rank.
     * Partial coverage returns false on purpose: half-sorting an option is how
     * you get a list that looks deliberate but isn't.
     */
    // ---- Numeric option columns (hardness test blocks) -------------------
    // Leading number of a value like "250 kg", "0.115", "60" or "140 - 160".
    function leadingNumber(v) {
        var m = String(v == null ? '' : v).trim().match(/^[0-9]*\.?[0-9]+/);
        return m ? parseFloat(m[0]) : null;
    }

    // Load runs DESCENDING and Range ASCENDING. That looks inconsistent next to
    // every other column here, and it is deliberate: it's how hardness test
    // block tables are printed in the trade (heaviest load first, lightest
    // hardness first). Confirmed by Robert 2026-08-10 — do not "fix" it.
    var NUMERIC_OPTIONS = { 'load': -1, 'range': 1 }; // option name -> sort direction

    function numericDirection(optName) {
        var d = NUMERIC_OPTIONS[(optName || '').trim().toLowerCase()];
        return d == null ? 0 : d;
    }

    function isSizeName(optName) {
        var n = (optName || '').trim().toLowerCase();
        return n === 'size' || n === 'size range';
    }

    function hasCanonicalOrder(optName, values) {
        var n = (optName || '').trim().toLowerCase();
        if (!values || values.length < 2) return false;

        if (isSizeName(n)) {
            // Bail out if any value is a format sizeKey can't read (e.g. the
            // BSPP "G1/8" series) -- those would all pile into one group and
            // get shuffled by the tiebreak.
            var infer = !usesExplicitGauge(values);
            return values.every(function (v) { return sizeKey(v, infer)[0] !== G_UNPARSED; });
        }
        if (n === 'member') {
            return values.every(function (v) { return MEMBER_ORDER[normalize(v)] != null; });
        }
        if (n === 'class') {
            return values.every(function (v) { return CLASS_ORDER[normalize(v)] != null; });
        }
        if (numericDirection(n) !== 0) {
            return values.every(function (v) { return leadingNumber(v) !== null; });
        }
        // Any option name at all may carry L1/L2/L3/6-Step values -- it's
        // "Step Type" on some products and "Type" on others.
        return isStepLike(values);
    }

    /**
     * Build a comparator for one option from its complete value set, or null
     * when the option has no canonical order (the caller must then leave it
     * exactly as stored). The full set is required because Size ordering
     * depends on it -- see groupRanks() and usesExplicitGauge().
     */
    function makeComparator(optName, values) {
        if (!hasCanonicalOrder(optName, values)) return null;
        var n = (optName || '').trim().toLowerCase();

        if (isSizeName(n)) {
            var infer = !usesExplicitGauge(values);
            var ranks = groupRanks(values, infer);
            return function (av, bv) {
                var ka = sizeKey(av, infer), kb = sizeKey(bv, infer);
                var ra = ranks[ka[0]], rb = ranks[kb[0]];
                if (ra !== rb) return ra - rb;
                if (ka[1] !== kb[1]) return ka[1] - kb[1];
                if (ka[2] !== kb[2]) return ka[2] - kb[2];
                return naturalCompare(av, bv);
            };
        }
        if (n === 'member') {
            return function (av, bv) {
                return MEMBER_ORDER[normalize(av)] - MEMBER_ORDER[normalize(bv)]
                    || naturalCompare(av, bv);
            };
        }
        if (n === 'class') {
            return function (av, bv) {
                return CLASS_ORDER[normalize(av)] - CLASS_ORDER[normalize(bv)]
                    || naturalCompare(av, bv);
            };
        }
        var dir = numericDirection(n);
        if (dir !== 0) {
            return function (av, bv) {
                var d = (leadingNumber(av) - leadingNumber(bv)) * dir;
                return d || naturalCompare(av, bv);
            };
        }
        return function (av, bv) { return stepRank(av) - stepRank(bv); };
    }

    /**
     * Sorted copy of `values`, or the SAME array back when the option has no
     * canonical order. Returning the original is the signal to leave it alone.
     */
    function sortValues(optName, values) {
        var cmp = makeComparator(optName, values);
        return cmp ? values.slice().sort(cmp) : values;
    }

    return {
        naturalCompare: naturalCompare,
        normalize: normalize,
        sizeKey: sizeKey,
        stepRank: stepRank,
        hasCanonicalOrder: hasCanonicalOrder,
        makeComparator: makeComparator,
        sortValues: sortValues,
    };
})();
